// middleware/auth.global.js

const PUBLIC_PAGES = [
  "/join/service-intro",
  "/join/terms-agree",
  "/join/permission",
  "/join/accessibility-guide",
  "/join/verify",
  "/join/captcha",
  "/join/plan",
  "/join/terms",
  "/join",
  "/home-free",
  "/join/signup-free",
  "/lock-screen",
  "/bye",
  "/mypage/cancel/complete",
  "/login",
  "/login/captcha",
  "/authfail",
  "/error",
  "/guide",
  "/auth/kakao/callback",
  "/auth/naver/callback",
  "/auth/google/callback",
  "/auth/apple/callback",
  "/magazine/financial-magazine",
  "/magazine/financial-detail",
  "/magazine/list",
  "/signup",
  "/refund",
  "/privacyPolicy",
  "/guest",
]

// 하위 경로까지 공개인 페이지. /bye/skt 처럼 통신사를 경로로 받는다.
// PUBLIC_PAGES 는 정확히 일치하는 경로만 통과시키므로 여기서 따로 본다.
const PUBLIC_PAGE_PREFIXES = [
  "/bye",
  "/refund",
]

// 로컬 테스트용. localhost 개발 서버에서만 브라우저로 앱 화면을 연다. 끝나면 이 함수를 뺀다.
const allowAppScreenOnLocalWeb = () => {
  if (!import.meta.dev || typeof window === 'undefined') return false
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
}

export default defineNuxtRouteMiddleware((to, from) => {
  const path = appPath(to.path)

  if (path.startsWith('/api/')) {
    return
  }

  // 앱 웹뷰(APP_COVER_CODES)인지. PIN 잠금과 홈 분기를 가르는 기준이다.
  // 로컬 개발 서버는 브라우저에서도 앱 화면을 연다.
  const isApp = allowAppScreenOnLocalWeb() || APP_COVER_CODES.includes(readCoverGbnCd())

  // PIN 잠금은 앱 전용 기능이다. (호스트앱 / 게스트앱)
  // 브라우저로 주소를 직접 쳐서 들어와도 화면을 열지 않는다.
  // 게스트 경로는 바로 아래에서 통과시키므로 그 앞에서 막는다.
  if ((path === '/join/pin' || path === '/guest/pin') && !isApp) {
    console.log('핀번호 화면 + 앱이 아님 - /homepage로 리다이렉트')
    return navigateTo('/homepage', { replace: true })
  }

  if (isGuestPath(path)) {
    return
  }

  // 호스트 앱 설치 링크. 로그인·핀과 관계없이 스토어로만 보낸다.
  if (path === '/host/link') {
    return
  }

  // 홈페이지(PC 웹)는 homepage.global.js 에서 별도로 처리한다.
  if (isHomepagePath(path)) {
    return
  }

  if (path === '/intro') {
    return navigateTo('/login', { replace: true })
  }

  // 가입 화면을 벗어나면 인증 상태를 버려서, 다시 들어왔을 때 번호를 다시 입력할 수 있게 한다.
  if (import.meta.client) {
    const joinFlow = useJoinFlow()
    if (joinFlow.isJoinFlowPath(from.path) && !joinFlow.isJoinFlowPath(to.path)) {
      joinFlow.clear()
    }
  }

  // ssr: false 이므로 이 미들웨어는 클라이언트에서만 실행된다.
  console.log('=== 라우팅 미들웨어 시작 ===')
  console.log('이동 경로:', path)

  try {
    const authState = useAuthState()
    const hasAuth = !!authState.token.value
    const hasKcbAuth = !!authState.isKcbAuthed.value

    console.log(hasAuth ? '✅ 토큰 확인' : '⚠️ 인증 없음')

    // 루트 경로 처리
    if (path === '/') {
      if (!isApp) {
        console.log('루트 경로 + 웹 접근 - /homepage로 리다이렉트')
        return navigateTo('/homepage', { replace: true })
      }
      if (hasAuth) {
        console.log('루트 경로 + 로그인됨 - 핀번호 확인')
        return navigateTo('/join/pin?mode=unlock', { replace: true })
      }
      console.log('루트 경로 + 비로그인 - /login으로 리다이렉트')
      return navigateTo('/login', { replace: true })
    }

    // 앱 전용 화면에 브라우저로 들어오면 홈페이지 쪽으로 보낸다.
    // (/login 은 공개 페이지라 토큰이 없어도 들어올 수 있어 여기서 따로 가른다)
    if (!isApp) {
      if (path === '/login') {
        console.log('앱 전용 로그인 화면 + 웹 접근 - 홈페이지로 리다이렉트')
        return navigateTo(hasAuth ? '/homepage' : '/homepage/login', { replace: true })
      }
      if (path === '/main') {
        console.log('앱 전용 홈 + 웹 접근 - /homepage로 리다이렉트')
        return navigateTo('/homepage', { replace: true })
      }
    }

    // 이미 로그인한 상태에서 로그인·가입 화면에 들어온 경우. 각자의 서비스 화면으로 보낸다.
    // 여기서 PIN 잠금해제로 보내지 않는다. 잠금해제는 앱 재진입(루트) 에서만 거친다.
    //
    // 필수 권한 게이트(/join/permission?mode=required)와
    // 핀 재설정 휴대폰 인증(/join/verify?mode=pin-reset)은 로그인 후에도 머물러야 한다.
    const isRequiredPermissionGate =
      (path === '/join/permission' || path === '/join/accessibility-guide') &&
      String(to.query.mode || '') === 'required'
    const isPinResetAuth =
      (path === '/join/verify' || path === '/join/captcha') &&
      String(to.query.mode || '') === 'pin-reset'

    if (
      hasAuth &&
      !isRequiredPermissionGate &&
      !isPinResetAuth &&
      (path === '/login' ||
        path === '/join' ||
        path === '/join/permission' ||
        path === '/join/accessibility-guide' ||
        path === '/join/verify' ||
        path === '/join/captcha' ||
        path === '/join/plan' ||
        path === '/join/terms')
    ) {
      if (isApp) {
        console.log('이미 로그인됨 + 앱 접근 - /main으로 리다이렉트')
        return navigateTo('/main', { replace: true })
      }
      console.log('이미 로그인됨 + 웹 접근 - /homepage로 리다이렉트')
      return navigateTo('/homepage', { replace: true })
    }

    if (hasKcbAuth && (path === '/block-service')) {
      console.log('이미 KCB 인증됨 - /main으로 리다이렉트')
      return navigateTo('/main', { replace: true })
    }

    // 공개 페이지 체크
    const isPublic = PUBLIC_PAGES.some((page) => path === page)
      || PUBLIC_PAGE_PREFIXES.some((page) => path.startsWith(`${page}/`))

    if (isPublic) {
      console.log('공개 페이지 - 통과')
      return
    }

    // 인증 필요한 페이지
    if (!hasAuth) {
      console.log('인증 없음 - 로그인으로 리다이렉트')

      const coverGbnCd = sessionStorage.getItem("coverGbnCd")
      const isApp = coverGbnCd === "UPASSAPP" || coverGbnCd === "KPASSAPP"
      return navigateTo(isApp ? "/authfail" : "/login", { replace: true })
    }

    console.log('인증 확인 완료 - 통과')

  } catch (error) {
    console.error('라우팅 미들웨어 에러:', error)
    return navigateTo('/error')
  }
})